import * as THREE from 'three';
import { Player } from '../entities/Player.js';
import { Enemy } from '../entities/Enemy.js';
import { CONFIG, WEAPON_TYPES, UPGRADES } from './config.js';
import { HUDManager } from '../ui/HUDManager.js';
import { AudioManager } from '../audio/AudioManager.js';
import { ParticleSystem } from '../systems/ParticleSystem.js';
import { objectPool } from '../systems/ObjectPool.js';
import { metaProgression } from '../systems/MetaProgression.js';

export class Game {
    constructor(characterData = null) {
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.hudManager = null;
        this.audioManager = new AudioManager();
        this.particleSystem = null;
        this.characterData = characterData;
        
        this.gameState = {
            player: null,
            enemies: [],
            bullets: [],
            enemyBullets: [],
            experiences: [],
            powerups: [],
            stats: {
                health: 100,
                maxHealth: 100,
                level: 1,
                experience: 0,
                experienceToNext: 15,
                time: 0,
                kills: 0
            },
            gameRunning: true,
            gameOver: false,
            lastSpawn: 0,
            lastBossSpawn: 0,
            difficultyMultiplier: 1
        };
        
        // Bind methods to preserve 'this' context
        this.animate = this.animate.bind(this);
        this.levelUp = this.levelUp.bind(this);
    }
    
    async init() {
        this.setupScene();
        this.setupCamera();
        this.setupRenderer();
        this.setupLighting();
        this.setupBackground();
        
        // Initialize systems
        this.particleSystem = new ParticleSystem(this.scene);
        
        // Create player with character data
        this.gameState.player = new Player(0, 0, this.scene, this.characterData);
        
        // Apply character stats to game state
        if (this.characterData) {
            this.gameState.stats.maxHealth = Math.floor(this.gameState.stats.maxHealth * this.characterData.stats.healthMod);
            this.gameState.stats.health = this.gameState.stats.maxHealth;
        }
        
        // Apply meta progression upgrades
        metaProgression.startRun();
        metaProgression.applyUpgradesToPlayer(this.gameState.player, this.gameState.stats);
        
        // Set global references for compatibility
        window.gameCamera = this.camera;
        window.gamePlayer = this.gameState.player;
        window.gameAudio = this.audioManager;
        window.gameParticles = this.particleSystem;
        
        // Setup HUD
        this.hudManager = new HUDManager(this.gameState);
        
        // Setup event listeners
        this.setupEventListeners();
        
        // Start game loop
        this.animate();
        
        console.log('ASCII Survivors initialized successfully!');
    }
    
    setupScene() {
        this.scene = new THREE.Scene();
        // Increased fog distance for better visibility
        this.scene.fog = new THREE.Fog(CONFIG.COLORS.BACKGROUND, 40, 100);
    }
    
    setupCamera() {
        this.camera = new THREE.PerspectiveCamera(
            65, 
            window.innerWidth / window.innerHeight, 
            0.1, 
            1000
        );
        this.camera.position.set(0, CONFIG.CAMERA_HEIGHT, CONFIG.CAMERA_DISTANCE);
        this.camera.lookAt(0, 0, 0);
    }
    
    setupRenderer() {
        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        document.getElementById('gameContainer').appendChild(this.renderer.domElement);
    }
    
    setupLighting() {
        // Brighter ambient lighting for better visibility
        const ambientLight = new THREE.AmbientLight(0x606060, 1.2);
        this.scene.add(ambientLight);
        
        // Add directional light for depth and better character visibility
        const directionalLight = new THREE.DirectionalLight(0x88ccff, 0.6);
        directionalLight.position.set(10, 20, 10);
        directionalLight.castShadow = false; // Keep performance good
        this.scene.add(directionalLight);
        
        // Add subtle point light that follows the player
        this.playerLight = new THREE.PointLight(0x00ff88, 0.4, 30);
        this.playerLight.position.set(0, 8, 0);
        this.scene.add(this.playerLight);
    }
    
    setupBackground() {
        // Animated background sphere
        const bgGeometry = new THREE.SphereGeometry(200, 32, 32);
        const bgMaterial = new THREE.MeshBasicMaterial({
            color: CONFIG.COLORS.BACKGROUND,
            side: THREE.BackSide
        });
        const bgMesh = new THREE.Mesh(bgGeometry, bgMaterial);
        this.scene.add(bgMesh);
        
        // Dynamic ground grid that follows player
        this.createDynamicGround();
    }
    
    createDynamicGround() {
        this.groundTiles = [];
        const tileSize = 100;
        const gridSize = 5; // 5x5 grid of tiles
        
        for (let x = -gridSize; x <= gridSize; x++) {
            for (let z = -gridSize; z <= gridSize; z++) {
                const groundGeometry = new THREE.PlaneGeometry(tileSize, tileSize, 20, 20);
                const groundMaterial = new THREE.MeshBasicMaterial({ 
                    color: 0x006688, 
                    transparent: true, 
                    opacity: 0.25,
                    wireframe: true
                });
                const ground = new THREE.Mesh(groundGeometry, groundMaterial);
                ground.rotation.x = -Math.PI / 2;
                ground.position.set(x * tileSize, 0, z * tileSize);
                this.scene.add(ground);
                this.groundTiles.push(ground);
            }
        }
    }
    
    updateDynamicGround() {
        if (!this.gameState.player || !this.groundTiles) return;
        
        const playerPos = this.gameState.player.position;
        const tileSize = 100;
        
        // Update ground tile positions to follow player
        let tileIndex = 0;
        for (let x = -2; x <= 2; x++) {
            for (let z = -2; z <= 2; z++) {
                if (tileIndex < this.groundTiles.length) {
                    const tile = this.groundTiles[tileIndex];
                    const targetX = Math.floor(playerPos.x / tileSize) * tileSize + x * tileSize;
                    const targetZ = Math.floor(playerPos.z / tileSize) * tileSize + z * tileSize;
                    tile.position.set(targetX, 0, targetZ);
                    tileIndex++;
                }
            }
        }
    }
    
    setupEventListeners() {
        window.addEventListener('resize', () => this.onWindowResize());
        
        // Add gameState to window for level up access
        this.gameState.levelUp = this.levelUp;
    }
    
    animate() {
        if (!this.gameState.gameRunning) return;
        
        requestAnimationFrame(this.animate);
        
        const deltaTime = 1/60; // Fixed timestep for consistency
        this.update(deltaTime);
        this.render();
    }
    
    update(deltaTime) {
        this.gameState.stats.time += deltaTime;
        
        // Update difficulty
        const minutes = this.gameState.stats.time / 60;
        this.gameState.difficultyMultiplier = 1 + (minutes * 0.15);
        
        // Update player
        if (this.gameState.player) {
            this.gameState.player.update(deltaTime, this.camera, this.gameState);
        }
        
        // Update enemies
        this.gameState.enemies.forEach(enemy => {
            enemy.update(deltaTime, this.camera, this.gameState);
        });
        
        // Update player bullets
        this.gameState.bullets = this.gameState.bullets.filter(bullet => {
            const shouldRemove = bullet.update(deltaTime, this.camera, this.gameState);
            if (shouldRemove) {
                bullet.destroy();
                return false;
            }
            return true;
        });
        
        // Update enemy bullets
        this.gameState.enemyBullets = this.gameState.enemyBullets.filter(bullet => {
            const shouldRemove = bullet.update(deltaTime, this.camera, this.gameState);
            if (shouldRemove) {
                bullet.destroy();
                return false;
            }
            return true;
        });
        
        // Update experiences
        this.gameState.experiences = this.gameState.experiences.filter(exp => {
            const shouldRemove = exp.update(deltaTime, this.camera, this.gameState);
            return !shouldRemove;
        });
        
        // Update powerups
        this.gameState.powerups = this.gameState.powerups.filter(powerup => {
            const shouldRemove = powerup.update(deltaTime, this.camera, this.gameState);
            return !shouldRemove;
        });
        
        // Update particle system
        if (this.particleSystem) {
            this.particleSystem.update(deltaTime);
        }
        
        // Spawn enemies
        this.updateEnemySpawning(deltaTime);
        
        // Spawn bosses
        this.updateBossSpawning(deltaTime);
        
        // Update camera
        this.updateCamera();
        this.updateDynamicGround();
        
        // Update HUD
        this.hudManager.update();
        
        // Check game over
        if (this.gameState.gameOver || this.gameState.stats.health <= 0) {
            this.endGame();
        }
    }
    
    updateEnemySpawning(deltaTime) {
        const minutes = this.gameState.stats.time / 60;
        const baseSpawnRate = CONFIG.INITIAL_SPAWN_RATE;
        const currentSpawnRate = baseSpawnRate / (1 + minutes * 0.4);
        
        if (this.gameState.stats.time - this.gameState.lastSpawn > currentSpawnRate && 
            this.gameState.enemies.length < CONFIG.MAX_ENEMIES) {
            this.spawnEnemy();
            this.gameState.lastSpawn = this.gameState.stats.time;
        }
    }
    
    spawnEnemy() {
        if (!this.gameState.player) return;
        
        const angle = Math.random() * Math.PI * 2;
        const distance = 18 + Math.random() * 12;
        const x = this.gameState.player.position.x + Math.cos(angle) * distance;
        const y = this.gameState.player.position.z + Math.sin(angle) * distance;
        
        const enemy = Enemy.spawnRandomType(
            x, y, 
            this.scene, 
            this.gameState.stats.time, 
            this.gameState.difficultyMultiplier
        );
        
        this.gameState.enemies.push(enemy);
    }
    
    updateBossSpawning(deltaTime) {
        const minutes = this.gameState.stats.time / 60;
        
        // Spawn mini-boss every 2 minutes starting at minute 3
        if (minutes >= 3 && this.gameState.stats.time - this.gameState.lastBossSpawn > 120) {
            this.spawnBoss();
            this.gameState.lastBossSpawn = this.gameState.stats.time;
        }
    }
    
    spawnBoss() {
        if (!this.gameState.player) return;
        
        const angle = Math.random() * Math.PI * 2;
        const distance = 25;
        const x = this.gameState.player.position.x + Math.cos(angle) * distance;
        const y = this.gameState.player.position.z + Math.sin(angle) * distance;
        
        const boss = new Enemy(x, y, 'mini_boss', this.scene, this.gameState.difficultyMultiplier);
        this.gameState.enemies.push(boss);
        
        // Play boss spawn sound
        this.audioManager.playBossSpawn();
        
        // Show boss notification
        this.hudManager.showMessage('⚠ MINI-BOSS SPAWNED ⚠', 3000, 'boss-warning');
    }
    
    updateCamera() {
        if (!this.gameState.player) return;
        
        const targetPos = this.gameState.player.position.clone();
        this.camera.position.x += (targetPos.x - this.camera.position.x) * 0.08;
        this.camera.position.z += (targetPos.z + CONFIG.CAMERA_DISTANCE - this.camera.position.z) * 0.08;
        this.camera.lookAt(targetPos);
        
        // Update player light position to follow player
        if (this.playerLight) {
            this.playerLight.position.x = targetPos.x;
            this.playerLight.position.z = targetPos.z;
        }
    }
    
    render() {
        this.renderer.render(this.scene, this.camera);
    }
    
    levelUp() {
        this.gameState.stats.level++;
        this.gameState.stats.experience -= this.gameState.stats.experienceToNext;
        this.gameState.stats.experienceToNext = Math.floor(this.gameState.stats.experienceToNext * 1.08);
        
        // Play level up sound
        this.audioManager.playLevelUp();
        
        // Show level up effect
        this.showLevelUpEffect();
        
        // Create particle effect
        if (this.particleSystem && this.gameState.player) {
            this.particleSystem.createLevelUpEffect(
                this.gameState.player.position.x, 
                this.gameState.player.position.z
            );
        }
        
        // Update meta progression
        if (metaProgression.currentRunStats) {
            metaProgression.currentRunStats.level = this.gameState.stats.level;
            metaProgression.currentRunStats.maxLevel = Math.max(
                metaProgression.currentRunStats.maxLevel, 
                this.gameState.stats.level
            );
        }
        
        // Show upgrade menu
        this.showUpgradeMenu();
    }
    
    showLevelUpEffect() {
        const levelUpEl = document.createElement('div');
        levelUpEl.className = 'level-up-effect';
        levelUpEl.textContent = `LEVEL ${this.gameState.stats.level}`;
        document.getElementById('gameContainer').appendChild(levelUpEl);
        
        setTimeout(() => {
            if (levelUpEl.parentNode) levelUpEl.parentNode.removeChild(levelUpEl);
        }, 1000);
    }
    
    showUpgradeMenu() {
        this.gameState.gameRunning = false;
        const menu = document.getElementById('upgradeMenu');
        const options = document.getElementById('upgradeOptions');
        
        const availableUpgrades = this.getAvailableUpgrades();
        const selectedUpgrades = [];
        
        // Select 3 random upgrades from available ones
        for (let i = 0; i < 3 && availableUpgrades.length > 0; i++) {
            const index = Math.floor(Math.random() * availableUpgrades.length);
            selectedUpgrades.push(availableUpgrades.splice(index, 1)[0]);
        }
        
        options.innerHTML = '';
        selectedUpgrades.forEach(upgrade => {
            const option = document.createElement('div');
            option.className = 'upgrade-option';
            
            let description = upgrade.desc;
            if (typeof upgrade.desc === 'function') {
                const currentLevel = this.gameState.player.getWeaponLevel(upgrade.weaponType);
                description = upgrade.desc(currentLevel);
            }
            
            option.innerHTML = `<strong>▶ ${upgrade.name}</strong><br><span style="color: #88ccff; font-size: 12px;">${description}</span>`;
            option.addEventListener('click', () => {
                upgrade.effect(this.gameState.player, this.gameState.stats);
                menu.style.display = 'none';
                this.gameState.gameRunning = true;
                this.animate();
            });
            options.appendChild(option);
        });
        
        menu.style.display = 'block';
    }
    
    getAvailableUpgrades() {
        const available = [];
        
        // Add weapon upgrades
        UPGRADES.filter(u => u.type === 'weapon').forEach(upgrade => {
            const currentLevel = this.gameState.player.getWeaponLevel(upgrade.weaponType);
            
            // Allow weapon if player doesn't have it or if it's not max level
            if (currentLevel === 0 && this.gameState.player.activeWeapons.size < this.gameState.player.weaponSlots) {
                // New weapon
                available.push(upgrade);
            } else if (currentLevel > 0 && currentLevel < 5) {
                // Weapon upgrade
                available.push(upgrade);
            }
        });
        
        // Add general upgrades (always available)
        UPGRADES.filter(u => u.type === 'general').forEach(upgrade => {
            available.push(upgrade);
        });
        
        return available;
    }
    
    endGame() {
        this.gameState.gameRunning = false;
        const minutes = Math.floor(this.gameState.stats.time / 60);
        const seconds = Math.floor(this.gameState.stats.time % 60);
        
        // End meta progression tracking
        const fragmentsEarned = metaProgression.endRun(
            this.gameState.stats.time,
            this.gameState.stats.level,
            this.gameState.stats.kills
        );
        
        setTimeout(() => {
            alert(`▓▓▓ SYSTEM CORRUPTED ▓▓▓\n\nSurvival Duration: ${minutes}:${seconds.toString().padStart(2, '0')}\nThreats Eliminated: ${this.gameState.stats.kills}\nLevel Achieved: ${this.gameState.stats.level}\nData Fragments Earned: ${fragmentsEarned}\n\nRestarting debug session...`);
            location.reload();
        }, 500);
    }
    
    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }
}